import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { Autocomplete, createFilterOptions } from '@mui/material'
import { useFormContext, Controller } from 'react-hook-form'

import AddIcon from '@mui/icons-material/Add'

import { theme } from '../../themes/theme'
import { MUTED_TEXT_DARK } from '../styles/colors'
import { TextField, SelectMenuItem } from '../styles/TextField'
import { InputWrapperDiv } from '../styles/Containers'
import AutocompletePopupPaper from './AutocompletePopupPaper'
import Label from './Label'

const StyledAutocomplete = styled(Autocomplete)`
  width: 100%;

  .MuiTextField-root {
    width: 100%;
  }

  && .MuiInputBase-root.MuiInput-root {
    padding: 0;
    flex-wrap: nowrap;
  }

  && .MuiAutocomplete-input {
    width: 0;
    min-width: 0;
    flex-grow: 1;
    padding: 8px 40px 8px 15px;
  }

  && .MuiAutocomplete-endAdornment {
    top: 50%;
    transform: translateY(-50%);
    right: 9px;
  }
`

const getError = (id, errors) => {
  return id.split('.').reduce((o, i) => {
    const exists = o[i] !== undefined
    if (!exists) return {}
    return o[i]
  }, errors)
}

const filter = createFilterOptions()

function getStringOption(options, value) {
  if (value === '' || value === null || value === undefined) return null
  return options.find(option => option.label === value) || { id: value, label: value }
}

const IconMenuItem = styled(SelectMenuItem)`
  gap: 10px;

  .MuiSvgIcon-root {
    width: 20px;
    height: 20px;
    color: ${MUTED_TEXT_DARK};
  }
`

const CreateMenuItem = styled(IconMenuItem)`
  color: ${theme.colors.primary};
  font-weight: 500 !important;

  .MuiSvgIcon-root {
    color: ${theme.colors.primary};
  }
`

function AutocompleteSelect({
  required = false,
  id,
  label = '',
  options,
  defaultValue = '',
  disabled = false,
  tooltip = '',
  basis,
  minwidth,
  storeStringValue = false,
  props,
  optionIcon,
  createOptionLabel
}) {
  const { formState, control } = useFormContext()

  const errors = formState.errors
  const error = getError(id, errors)

  return (
    <InputWrapperDiv basis={basis} minwidth={minwidth}>
      <Controller
        control={control}
        name={id}
        defaultValue={defaultValue}
        render={({ field: { onChange, value, ref } }) => (
          <StyledAutocomplete
            id={id}
            name={id}
            fullWidth
            freeSolo={storeStringValue}
            forcePopupIcon
            slots={{ paper: AutocompletePopupPaper }}
            disableClearable={storeStringValue && label === ''}
            value={storeStringValue ? getStringOption(options, value) : value}
            inputValue={storeStringValue ? value || '' : undefined}
            options={options}
            getOptionLabel={option => {
              if (typeof option === 'string') {
                return option
              }
              if (option.inputValue) {
                return option.inputValue
              }
              return option.label
            }}
            getOptionKey={option => option.id || option.inputValue || option.label}
            isOptionEqualToValue={(option, selected) => {
              if (!selected) return false
              if (storeStringValue) return option.label === selected.label
              return option.id === selected.id
            }}
            disabled={disabled}
            renderOption={(optionProps, option) => {
              const { key, ...restOptionProps } = optionProps
              if (option.inputValue && createOptionLabel) {
                return (
                  <CreateMenuItem key={key || option.inputValue} {...restOptionProps}>
                    <AddIcon />
                    {option.label}
                  </CreateMenuItem>
                )
              }
              const MenuItemComponent = optionIcon ? IconMenuItem : SelectMenuItem
              return (
                <MenuItemComponent key={key || option.id || option.inputValue} {...restOptionProps}>
                  {!option.inputValue && optionIcon}
                  {option.label}
                </MenuItemComponent>
              )
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)
              if (storeStringValue) return filtered
              const { inputValue } = params
              const isExisting = options.some(option => inputValue.toLowerCase() === option.label.toLowerCase())
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  label: createOptionLabel ? createOptionLabel(inputValue) : `Add "${inputValue}"`
                })
              }
              return filtered
            }}
            onInputChange={(event, newInputValue) => {
              if (storeStringValue) {
                onChange(newInputValue)
              }
            }}
            onChange={(event, newValue) => {
              if (storeStringValue) return
              if (typeof newValue === 'string') {
                onChange({
                  label: newValue
                })
              } else if (newValue && newValue.inputValue) {
                onChange({
                  label: newValue.inputValue
                })
              } else {
                onChange(newValue)
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                inputRef={ref}
                variant="standard"
                fullWidth
                label={label !== '' && <Label label={label} tooltip={tooltip} />}
                InputLabelProps={{
                  shrink: true,
                  required,
                  focused: false
                }}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true
                }}
                inputProps={{
                  ...params.inputProps,
                  ...props?.inputProps
                }}
                haslabel={label === '' ? 'false' : 'true'}
                error={Object.keys(error).length > 0 && !!error}
                helperText={error?.message}
                FormHelperTextProps={{ role: 'alert' }}
              />
            )}
          />
        )}
      />
    </InputWrapperDiv>
  )
}

AutocompleteSelect.propTypes = {
  id: PropTypes.string.isRequired,
  required: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ]),
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  basis: PropTypes.string,
  minwidth: PropTypes.string,
  storeStringValue: PropTypes.bool,
  props: PropTypes.object,
  // Icon shown before each existing option (e.g. a folder for categories).
  optionIcon: PropTypes.node,
  // Custom label for the create-on-the-fly row; also switches it to the
  // highlighted style with a plus icon.
  createOptionLabel: PropTypes.func
}

export default AutocompleteSelect
